import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, FileText, Building2, Calendar, Search } from 'lucide-react';
import { SortSelect } from '../../components/SortSelect';
import { useServerList } from '../../hooks/useServerList';
import Pagination from '../../components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  firma_id: '', kurum_bina_ids: [], sozlesme_tipi: 'bakim',
  baslangic_tarihi: '', bitis_tarihi: '', maks_mudahale_suresi_saat: 12,
  sla_ihlal_ceza_gunluk: '', sla_ihlal_ceza_saatlik: '',
  aylik_hakedis_tutari: '', birim_kullanim_disi_kesinti: 500
};

export default function PKSozlesmeler() {
  const { user } = useAuth();
  const isViewer = !!user?.is_viewer;
  const [firmalar, setFirmalar] = useState([]);
  const [binalar, setBinalar] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({...EMPTY_FORM});
  const [submitting, setSubmitting] = useState(false);

  const SORT_MAP = {
    newest: 'created_at:desc', oldest: 'created_at:asc',
    firma_az: 'firma_unvan:asc', bitis_yakin: 'bitis_tarihi:asc',
    hakedis_yuksek: 'aylik_hakedis_tutari:desc', hakedis_dusuk: 'aylik_hakedis_tutari:asc',
  };
  const [sortKey, setSortKey] = useState('newest');

  const sozlesmeList = useServerList('/pk/sozlesmeler', {
    pageSize: 100,
    initialSort: 'created_at:desc',
  });

  useEffect(() => {
    sozlesmeList.setSort(SORT_MAP[sortKey] || 'created_at:desc');
  }, [sortKey]);// eslint-disable-line

  useEffect(() => { fetchMeta(); }, []);

  const fetchMeta = async () => {
    try {
      const [fRes, bRes] = await Promise.all([
        axios.get(`${API}/pk/firmalar`, { withCredentials: true }),
        axios.get(`${API}/pk/binalar`, { withCredentials: true })
      ]);
      setFirmalar(fRes.data);
      setBinalar(bRes.data);
    } catch (err) { console.error(err); }
    finally { setLoadingMeta(false); }
  };

  const handleSubmit = async () => {
    if (!formData.firma_id || !formData.baslangic_tarihi || !formData.bitis_tarihi) return;
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`${API}/pk/sozlesmeler/${editId}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/pk/sozlesmeler`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      setFormData({...EMPTY_FORM});
      sozlesmeList.refetch();
    } catch (err) { alert('Hata: ' + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  const handleEdit = (s) => {
    setFormData(s);
    setEditId(s.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const getFirmaAdi = (id) => firmalar.find(f => f.id === id)?.unvan || id;

  const getKalanGun = (bitis) => {
    if (!bitis) return null;
    return Math.ceil((new Date(bitis) - new Date()) / 86400000);
  };

  const formatMoney = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? '—' : num.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL';
  };

  if (loadingMeta) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  const sortedSozlesmeler = sozlesmeList.items;

  return (
    <div className="space-y-6" data-testid="pk-sozlesmeler">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Sözleşme Yönetimi</h2>
          <p className="text-sm text-gray-400">{sozlesmeList.total} sözleşme {sozlesmeList.loading && '· yükleniyor...'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={sozlesmeList.search}
              onChange={(e) => sozlesmeList.setSearch(e.target.value)}
              placeholder="Sözleşme no, konu, firma..."
              className="bg-gray-800 border-gray-700 text-white pl-8 h-9 w-[260px]"
              data-testid="sozlesme-search-input"
            />
          </div>
          <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
            { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
            { value: 'bitis_yakin', label: 'Bitiş Tarihi (Yakın)' },
            { value: 'hakedis_yuksek', label: 'Hakediş (Yüksek→Düşük)' }, { value: 'hakedis_dusuk', label: 'Hakediş (Düşük→Yüksek)' }
          ]} />
          {!isViewer && (
            <Button onClick={() => { setFormData({...EMPTY_FORM}); setEditMode(false); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-sozlesme-btn">
              <Plus className="w-4 h-4 mr-2" /> Sözleşme Ekle
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sortedSozlesmeler.map(s => {
          const kalan = getKalanGun(s.bitis_tarihi);
          return (
            <Card key={s.id} className={`bg-gray-900/80 border-gray-700/50 ${kalan !== null && kalan <= 90 ? 'border-orange-600/50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <h3 className="text-sm font-semibold text-white">{getFirmaAdi(s.firma_id)}</h3>
                      <Badge className={`text-[10px] ${s.sozlesme_tipi === 'bakim' ? 'bg-green-700' : s.sozlesme_tipi === 'periyodik_kontrol' ? 'bg-blue-700' : 'bg-purple-700'}`}>
                        {s.sozlesme_tipi === 'bakim' ? 'Bakım' : s.sozlesme_tipi === 'periyodik_kontrol' ? 'P.Kontrol' : 'Onarım'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mt-2">
                      <div><span className="text-gray-500">Başlangıç:</span> <span className="text-white">{s.baslangic_tarihi}</span></div>
                      <div><span className="text-gray-500">Bitiş:</span> <span className="text-white">{s.bitis_tarihi}</span></div>
                      <div><span className="text-gray-500">SLA:</span> <span className="text-white">{s.maks_mudahale_suresi_saat}h</span></div>
                      <div><span className="text-gray-500">Ceza (günlük):</span> <span className="text-white">{formatMoney(s.sla_ihlal_ceza_gunluk)}</span></div>
                      <div><span className="text-gray-500">Aylık Hakediş:</span> <span className="text-blue-300 font-medium">{formatMoney(s.aylik_hakedis_tutari)}</span></div>
                      <div>
                        {kalan !== null && (
                          <span className={`${kalan <= 30 ? 'text-red-400 font-bold' : kalan <= 90 ? 'text-orange-400' : 'text-gray-400'}`}>
                            {kalan > 0 ? `${kalan} gün kaldı` : 'Süresi dolmuş'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} className="text-gray-400 hover:text-white h-7 text-xs" style={{ display: isViewer ? 'none' : undefined }}>Düzenle</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Server-side pagination */}
      <Pagination
        page={sozlesmeList.page}
        totalPages={sozlesmeList.totalPages}
        total={sozlesmeList.total}
        pageSize={sozlesmeList.pageSize}
        loading={sozlesmeList.loading}
        onPageChange={sozlesmeList.setPage}
        onPageSizeChange={sozlesmeList.setPageSize}
        testIdPrefix="sozlesme-pagination"
      />

      {/* Form */}
      <Dialog open={dialogOpen} onOpenChange={v => setDialogOpen(v)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle data-testid="sozlesme-form-title">{editMode ? 'Sözleşme Düzenle' : 'Yeni Sözleşme'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Firma *</Label>
              <Select value={formData.firma_id} onValueChange={v => setFormData({...formData, firma_id: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">{firmalar.map(f => <SelectItem key={f.id} value={f.id}>{f.unvan}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sözleşme Tipi</Label>
              <Select value={formData.sozlesme_tipi} onValueChange={v => setFormData({...formData, sozlesme_tipi: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="bakim">Bakım</SelectItem><SelectItem value="periyodik_kontrol">Periyodik Kontrol</SelectItem><SelectItem value="onarim">Onarım</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Başlangıç *</Label>
                <Input type="date" value={formData.baslangic_tarihi} onChange={e => setFormData({...formData, baslangic_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Bitiş * (maks 3 yıl)</Label>
                <Input type="date" value={formData.bitis_tarihi} onChange={e => setFormData({...formData, bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Maks Müdahale (saat)</Label>
                <Input type="number" value={formData.maks_mudahale_suresi_saat} onChange={e => setFormData({...formData, maks_mudahale_suresi_saat: parseInt(e.target.value) || 12})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Aylık Hakediş (TL)</Label>
                <Input type="number" value={formData.aylik_hakedis_tutari} onChange={e => setFormData({...formData, aylik_hakedis_tutari: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Günlük Gecikme Cezası (TL)</Label>
                <Input type="number" value={formData.sla_ihlal_ceza_gunluk} onChange={e => setFormData({...formData, sla_ihlal_ceza_gunluk: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Saatlik Ceza (TL, opsiyonel)</Label>
                <Input type="number" value={formData.sla_ihlal_ceza_saatlik} onChange={e => setFormData({...formData, sla_ihlal_ceza_saatlik: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); setFormData({...EMPTY_FORM}); }} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">{submitting ? '...' : editMode ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
